/**
 * HeaderContentService
 * Orchestrates dynamic header content generation across different modes
 *
 * Architecture:
 * - Each mode provides a strategy via getHeaderContentStrategy()
 * - Strategy defines: content generation logic, update triggers, and update conditions
 * - This service coordinates between mode strategies and HeaderManager display
 *
 * Strategy Interface:
 * {
 *   generateContent(context): Promise<{
 *     leftBlock:   null | { title, rightText?, percent? },
 *     middleBlock:        { html: string }
 *   }>,
 *   getUpdateTriggers(): string[],
 *   shouldUpdate(event, context): boolean
 * }
 *
 * Legacy shim: strategies returning { text: '<html>' } are accepted and
 * normalized to { leftBlock: null, middleBlock: { html: text } }. Advice,
 * Treatment and Insight all still use that shape — the shim is load-bearing,
 * not deprecated scaffolding.
 *
 * A null leftBlock means "use the plain #header-mode-label span", whose text is
 * owned by HeaderManager.setModeLabel via mode:changed. This service never
 * writes that label; see the ownership table in HeaderManager's file header.
 */

export class HeaderContentService {
  /**
   * @param {HeaderManager} headerManager - Header display manager
   * @param {EventBus} eventBus - Event bus for communication
   */
  constructor(headerManager, eventBus) {
    this.headerManager = headerManager;
    this.eventBus = eventBus;

    // State
    this.currentMode = null;
    this.currentStrategy = null;
    this.triggerHandlers = []; // { trigger, handler } pairs for cleanup
    this.isGenerating = false;

    // Monotonic counter bumped on every activate/deactivate. updateContent
    // snapshots the token before its await and discards results if it changed,
    // preventing stale async strategy writes from leaking into the next mode.
    this._activationToken = 0;

    // Latest context that arrived while a generation was in flight. Cleared
    // and re-invoked in updateContent's finally block so legitimate updates
    // aren't silently dropped (replaces the old skip-and-drop behavior).
    this._pendingContext = null;

    // Block-aware payload caches — drive change detection per block.
    this._lastLeftBlock = null;
    this._lastMiddleBlock = null;
  }

  /**
   * Activate header content strategy for a mode
   * @param {BaseMode} mode - Mode instance with strategy
   */
  activateMode(mode) {
    // Bump the activation token first so any in-flight generation from the
    // previous mode discards its result instead of writing into this mode.
    this._activationToken++;

    // Deactivate previous mode if any
    if (this.currentMode) {
      this.deactivateMode();
    }

    this.currentMode = mode;

    // Get strategy from mode
    const strategy = mode.getHeaderContentStrategy ? mode.getHeaderContentStrategy() : null;

    if (!strategy) {
      console.log(`[HeaderContentService] Mode ${mode.name} has no header content strategy`);
      // Strategy-less activation: clear stale blocks. The plain label is left to
      // setActiveState, which fires on the same mode:changed.
      this._clearBlocks();
      return;
    }

    this.currentStrategy = strategy;

    // Register event listeners for update triggers, closing over trigger name
    const triggers = strategy.getUpdateTriggers ? strategy.getUpdateTriggers() : [];
    this.triggerHandlers = triggers.map(trigger => {
      const handler = (data) => this.handleUpdateTrigger(trigger, data);
      this.eventBus.on(trigger, handler);
      return { trigger, handler };
    });

    console.log(`[HeaderContentService] Activated strategy for ${mode.name}, triggers:`, triggers);

    // Generate initial content
    this.updateContent({ event: 'mode:enter', mode: mode.name });
  }

  /**
   * Deactivate current mode's strategy
   */
  deactivateMode() {
    if (!this.currentMode) return;

    // Bump token so any in-flight generation discards its stale result.
    this._activationToken++;
    this._pendingContext = null;

    // Unregister all event listeners
    this.triggerHandlers.forEach(({ trigger, handler }) => {
      this.eventBus.off(trigger, handler);
    });

    console.log(`[HeaderContentService] Deactivated strategy for ${this.currentMode.name}`);

    // Clear state + DOM so the next mode (or strategy-less activation) starts fresh.
    this.currentMode = null;
    this.currentStrategy = null;
    this.triggerHandlers = [];
    this._clearBlocks();
  }

  /**
   * Handle update trigger events
   * @param {string} eventName - The event bus trigger name
   * @param {Object} eventData - Event data from event bus
   */
  async handleUpdateTrigger(eventName, eventData) {
    if (!this.currentStrategy) return;

    // Check if strategy wants to update for this event
    const shouldUpdate = this.currentStrategy.shouldUpdate
      ? this.currentStrategy.shouldUpdate(eventName, eventData)
      : true;

    if (!shouldUpdate) {
      console.log(`[HeaderContentService] Strategy declined update for event:`, eventName);
      return;
    }

    // Generate new content
    await this.updateContent({ ...eventData, event: eventName });
  }

  /**
   * Generate and display new header content
   * @param {Object} context - Context data for content generation
   */
  async updateContent(context = {}) {
    if (!this.currentStrategy) return;

    // If a generation is already in flight, stash the latest context for a
    // rerun after the current one completes (last-write-wins). This replaces
    // the old skip-and-drop behavior, which silently lost legitimate updates.
    if (this.isGenerating) {
      this._pendingContext = context;
      return;
    }

    this.isGenerating = true;

    // Snapshot the activation token so we can detect if the mode changed
    // during the await below and discard stale results.
    const token = this._activationToken;

    try {
      console.log('[HeaderContentService] Generating content for context:', context);

      // Call strategy's content generation method
      const result = await this.currentStrategy.generateContent(context);

      // Stale-result guard: if a mode switch (or another deactivate) happened
      // during the await, drop this result instead of writing it into the new
      // mode's header.
      if (token !== this._activationToken) {
        console.log('[HeaderContentService] Discarding stale generation result (activation changed)');
        return;
      }

      const normalized = this._normalizeResult(result);
      if (!normalized) {
        console.warn('[HeaderContentService] Strategy returned invalid result:', result);
        return;
      }

      const { leftBlock, middleBlock } = normalized;

      // Dispatch per-block only when that block's payload actually changed.
      if (!this._leftBlockEqual(this._lastLeftBlock, leftBlock)) {
        this._lastLeftBlock = leftBlock;
        this.headerManager.setLeftBlock(leftBlock);
      }
      if (!this._middleBlockEqual(this._lastMiddleBlock, middleBlock)) {
        this._lastMiddleBlock = middleBlock;
        this.headerManager.setMiddleBlock(middleBlock);
      }

    } catch (error) {
      // Only handle the error if the activation is still current; otherwise
      // the error belongs to a deactivated strategy and isn't our problem.
      if (token === this._activationToken) {
        console.error('[HeaderContentService] Error generating content:', error);
        this._handleError(error);
      }
    } finally {
      this.isGenerating = false;

      // If updates came in while we were generating, run once more with the
      // most recent context. Token-guarded so the rerun doesn't fire if the
      // mode was switched out.
      const pending = this._pendingContext;
      this._pendingContext = null;
      if (pending && token === this._activationToken) {
        this.updateContent(pending).catch(err => {
          console.error('[HeaderContentService] Pending rerun failed:', err);
        });
      }
    }
  }

  /**
   * Handle content generation errors.
   *
   * Clears the MIDDLE block only. The left block carries mode identity, and a
   * failed content generation (e.g. a descriptor fetch timing out) is no reason
   * to blank out which mode the user is in.
   *
   * @param {Error} _error - Error object
   * @private
   */
  _handleError(_error) {
    this._lastMiddleBlock = null;
    if (this.headerManager) {
      this.headerManager.clearMiddleBlock();
    }
    console.log('[HeaderContentService] Cleared header middle block due to error');
  }

  /**
   * Normalize a strategy result to the two-block shape.
   * Accepts legacy { text } and { leftBlock, middleBlock }.
   * Returns null if the result is unusable.
   * @private
   */
  _normalizeResult(result) {
    if (!result || typeof result !== 'object') return null;

    // Legacy shape — { text: '<html>' } — treated as middle-block only.
    if (typeof result.text === 'string') {
      return {
        leftBlock: null,
        middleBlock: { html: result.text }
      };
    }

    // Current shape — must have a middleBlock with an html string.
    const middleBlock = result.middleBlock;
    if (!middleBlock || typeof middleBlock.html !== 'string') return null;

    // A leftBlock without a usable title falls back to the plain label.
    const candidate = result.leftBlock;
    const leftBlock = (candidate && typeof candidate === 'object' && candidate.title)
      ? candidate
      : null;

    return { leftBlock, middleBlock };
  }

  /**
   * Clear both blocks on the DOM and reset payload caches.
   * Called on deactivate, dashboard nav, and strategy-less activation.
   * @private
   */
  _clearBlocks() {
    this._lastLeftBlock = null;
    this._lastMiddleBlock = null;
    if (this.headerManager) {
      this.headerManager.clearLeftBlock();
      this.headerManager.clearMiddleBlock();
    }
  }

  /**
   * Shallow-equal comparison for left-block payloads.
   * @private
   */
  _leftBlockEqual(a, b) {
    if (a === b) return true;
    if (a === null || b === null) return false;
    return a.title === b.title
      && a.rightText === b.rightText
      && a.percent === b.percent;
  }

  /**
   * Equality for middle-block payloads.
   * @private
   */
  _middleBlockEqual(a, b) {
    const aHtml = a?.html ?? null;
    const bHtml = b?.html ?? null;
    return aHtml === bHtml;
  }

  /**
   * Get current mode name
   * @returns {string|null}
   */
  getCurrentMode() {
    return this.currentMode ? this.currentMode.name : null;
  }

  /**
   * Check if service has active strategy
   * @returns {boolean}
   */
  hasActiveStrategy() {
    return this.currentStrategy !== null;
  }

  /**
   * Force content regeneration (useful for debugging or manual updates)
   * @param {Object} context - Optional context override
   */
  async forceUpdate(context = {}) {
    console.log('[HeaderContentService] Force update requested');
    await this.updateContent(context);
  }

  /**
   * Set header to navigation state without deactivating the mode.
   * Used when navigating to dashboard while preserving session.
   *
   * Bumps the activation token so a generation still in flight cannot write its
   * result onto the dashboard-state header, then clears both blocks so
   * re-entering a different mode doesn't see stale content.
   */
  hide() {
    this._activationToken++;
    this._pendingContext = null;
    if (this.headerManager) {
      this.headerManager.setDashboardState();
    }
    this._clearBlocks();
  }
}
